import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const Container = styled.div`
  min-height: 100vh;
  background: #ffffff;
  padding: 2rem 1rem;
  position: relative;
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 0;
`;

const BackButtonContainer = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
  
  @media (max-width: 768px) {
    top: 16px;
    left: 16px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: transparent;
  border: none;
  color: #4f46e5;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s;
  
  &:hover {
    color: #4338ca;
  }
`;

const Title = styled.h1`
  color: #4f46e5;
  border-bottom: 2px solid #4f46e5;
  padding-bottom: 12px;
  margin-bottom: 16px;
  font-size: 2rem;
  font-weight: 700;
`;

const UpdateDate = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
`;

const Section = styled.section`
  margin-top: 2rem;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  color: #111827;
  margin-top: 1.75rem;
  margin-bottom: 0.5rem;
  font-size: 1.25rem;
  font-weight: 600;
`;

const Paragraph = styled.p`
  margin: 0.5rem 0;
  line-height: 1.75;
  color: #1f2937;
`;

const List = styled.ul`
  padding-left: 1.25rem;
  margin: 0.5rem 0;
`;

const ListItem = styled.li`
  margin: 0.375rem 0;
  line-height: 1.75;
  color: #1f2937;
`;

const Footer = styled.p`
  margin-top: 2.5rem;
  font-size: 0.875rem;
  color: #6b7280;
`;

const Link = styled.a`
  color: #4f46e5;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const TermsPage = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    // 如果历史记录中有上一页，返回上一页；否则返回首页
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <>
      <Helmet>
        <title>用户服务协议 - 职唛AI</title>
        <meta name="description" content="职唛AI用户服务协议 - 了解使用职唛AI服务的相关条款和规定" />
      </Helmet>
      <Container>
        <BackButtonContainer>
          <BackButton onClick={handleBack}>
            <ArrowLeft size={18} />
            返回
          </BackButton>
        </BackButtonContainer>
        <ContentWrapper>
          <Title>职唛AI用户服务协议</Title>
          <UpdateDate><strong>最后更新日期：2026年1月</strong></UpdateDate>

          <Paragraph>
            在使用职唛AI（以下简称"本产品"、"我们"）提供的服务之前，请您务必仔细阅读并理解本《用户服务协议》（以下简称"本协议"）。一旦您使用或继续使用本产品，即视为您已阅读、理解并同意受本协议约束。
          </Paragraph>

          <Section>
            <SectionTitle>一、定义与适用范围</SectionTitle>
            <List>
              <ListItem>职唛AI：指由杭州元烁智创科技有限公司运营的智能求职服务产品及相关网站、网页与功能。</ListItem>
              <ListItem>用户：指使用本产品的个人，包括注册用户与未注册的访问者。</ListItem>
              <ListItem>服务：包括但不限于 AI 求职助手对话、简历诊断与优化、面试准备与模拟面试、报告导出、语音识别/合成、简历模版下载等。</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>二、账户注册与登录</SectionTitle>
            <List>
              <ListItem>目前您可通过邮箱+密码或邮箱验证码登录；后续如开通手机号码或微信扫码登录功能，将以产品实际展示为准。首次成功登录即视为完成注册。</ListItem>
              <ListItem>您应保证所提供信息真实、准确、完整，并及时更新。</ListItem>
              <ListItem>您有责任妥善保管账户信息与验证码，不得将账户转让、出借或以其他方式提供给他人使用。</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>三、服务内容与开通</SectionTitle>
            <List>
              <ListItem>我们将持续优化包括简历诊断、押题训练、模拟面试、会议助手等功能，具体以实际提供为准。</ListItem>
              <ListItem>部分功能可能需要相应设备权限（如麦克风/摄像头）或文件上传，您同意开启或提供相应资料以使用相关功能。</ListItem>
              <ListItem>我们可能依据业务发展对服务进行调整、更新、中止或终止，并通过合理方式通知您。</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>四、付费、订阅与退款</SectionTitle>
            <List>
              <ListItem>如存在付费/订阅服务，以页面公示的价格、权益与规则为准。</ListItem>
              <ListItem>在法律法规允许的范围内，已开通的虚拟类服务一般不支持无理由退款；若确有质量问题，将依照平台公示规则并结合适用的法律法规进行处理，本条款不影响您作为消费者依法享有的法定权利。</ListItem>
              <ListItem>我们可能提供优惠活动或邀请码权益，具体以活动规则为准。</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>五、用户行为规范</SectionTitle>
            <List>
              <ListItem><strong>合法合规使用：</strong>不得利用本产品从事任何违法违规、侵害他人合法权益或违反公序良俗的行为。您承诺对您输入、上传或生成的内容承担全部责任。</ListItem>
              <ListItem><strong>禁止作弊与欺诈：</strong>严禁在任何正式考试、面试、竞赛或其他要求独立完成的场景中使用本产品进行作弊、辅助答题或欺诈。一旦发现此类行为，我们有权立即封禁账号，并配合相关机构调查，且不退还任何费用。</ListItem>
              <ListItem><strong>系统安全：</strong>不得对本产品进行逆向工程、爬虫抓取、未经授权的自动化访问、攻击、干扰或破坏。</ListItem>
              <ListItem><strong>内容规范：</strong>不得上传、生成或传播违法违规信息或侵权内容（包括但不限于侵犯隐私、知识产权、商业秘密、色情、暴力、政治敏感信息）。</ListItem>
              <ListItem><strong>数据授权：</strong>如您授权我们处理您的简历、音视频或其他资料，您保证已对相关资料享有合法权利。您不得上传他人的敏感隐私数据。</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>六、用户内容与知识产权</SectionTitle>
            <List>
              <ListItem>除法律另有规定或双方另行约定外，您对自行上传的内容（如简历）保留合法权利。</ListItem>
              <ListItem>基于您的输入/上传而由系统生成的内容（如报告、题目与建议）中所包含的算法/模型与服务框架的权利归我们或相应权利人所有；在法律允许范围内，您获得合理使用权。</ListItem>
              <ListItem>未经书面许可，不得以任何方式复制、传播、销售或用于竞争性服务的构建。</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>七、隐私与数据保护</SectionTitle>
            <Paragraph>
              我们将依据《隐私协议》处理您的个人信息，包括收集目的、范围、使用、共享与保护等内容。我们承诺不主动收集您的敏感身份信息，并采取安全措施保护您的数据。详情请参阅《<Link href="/privacy" onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}>隐私协议</Link>》。
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>八、服务可用性与变更</SectionTitle>
            <List>
              <ListItem>我们将合理维护服务稳定，但不对不间断可用作出保证。</ListItem>
              <ListItem>因系统维护、升级、网络或不可抗力导致的中断/障碍，我们将尽力修复与恢复，但不对由此产生的损失承担赔偿责任，除非法律另有规定。</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>九、免责声明</SectionTitle>
            <List>
              <ListItem><strong>AI技术局限性：</strong>本产品提供的内容与建议（包括面试回答、评分、职业建议等）基于深度学习算法与模型输出，具有一定的随机性与局限性（可能出现“幻觉”或错误），仅供参考。<strong>不构成对招聘结果、职业发展、法律或投资的专业建议或保证。</strong></ListItem>
              <ListItem><strong>自主决策：</strong>您应基于自身实际情况与独立判断作出决策，并自行承担相应风险。我们不对您因使用本产品生成的建议而导致的任何求职失败、职业损失或其他后果承担责任。</ListItem>
              <ListItem><strong>第三方风险：</strong>本产品可能包含指向第三方网站或服务的链接，我们不对第三方的服务或内容负责。</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>十、责任限制</SectionTitle>
            <Paragraph>
              在适用法律允许的最大范围内，对于因使用或无法使用本产品而造成的任何间接、附带、特殊或惩罚性损害，我们不承担责任；对于直接经济损失，总赔偿责任以您就相关服务已支付的费用为上限（如有）。
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>十一、违约与处理</SectionTitle>
            <Paragraph>
              如我们发现或收到他人举报您存在违反法律法规或本协议的行为，我们有权视情节采取警告、限制/冻结功能、删除内容、暂停或终止服务等措施，并保留依法追究的权利。
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>十二、协议的终止</SectionTitle>
            <Paragraph>
              我们有权基于合规、安全或业务调整等原因终止向您提供全部或部分服务；您也可通过产品内设置或联系客服申请注销账户。协议终止并不影响在终止前已产生的权利义务。
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>十三、适用法律与争议解决</SectionTitle>
            <Paragraph>
              本协议适用中华人民共和国法律。因本协议引发或与本协议有关的争议，由双方友好协商解决；协商不成的，提交我们所在地有管辖权的人民法院诉讼解决。
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>十四、其他</SectionTitle>
            <List>
              <ListItem>本协议的部分条款被认定无效或不可执行的，不影响其余条款的效力。</ListItem>
              <ListItem>我们可能基于业务需要对本协议进行修订，修订后将通过合理方式通知您；如您继续使用即视为同意修订内容。</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>十五、联系我们</SectionTitle>
            <Paragraph>运营主体：杭州元烁智创科技有限公司</Paragraph>
            <Paragraph>地址：浙江省杭州市萧山区德必港立WE产业园A座315室</Paragraph>
            <Paragraph>联系渠道：应用内反馈或客服邮箱 contact@zhispark.com</Paragraph>
          </Section>

          <Footer>
            本协议与《隐私协议》构成您与职唛AI之间关于使用服务的完整协议。
          </Footer>
        </ContentWrapper>
      </Container>
    </>
  );
};

export default TermsPage;
